import {
  AfterContentChecked,
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
  inject,
  signal,
  WritableSignal
} from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { BusinessPitch, ForgotPasswordModel } from '@App/common/models';
import { environment } from '@Environments';
import { BusinessService, UserCommonApiService } from '@App/common/services';
import { AppSettings } from '@Shared/app.settings';
import { AuthActionType } from '../../auth.type';
import { CommonModule, NgIf } from '@angular/common';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { TranslateModule } from '@ngx-translate/core';
import { NoLeadingSpaceDirective } from '@App/common/directives';

@Component({
  selector: 'app-forgot-password',
  templateUrl: './forgot-password.component.html',
  styleUrls: ['./forgot-password.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    InputTextModule,
    ButtonModule,
    TranslateModule,
    NgIf,
    NoLeadingSpaceDirective
  ],
  encapsulation: ViewEncapsulation.Emulated
})
export class ForgotPasswordComponent
  implements OnInit, AfterViewInit, AfterContentChecked
{
  @Output() activeTypeChange: EventEmitter<AuthActionType> = new EventEmitter();
  forgotPasswordForm: UntypedFormGroup;
  submitted: boolean = false;
  loading: boolean = false;
  errorMessage: string;

  // view child and business-related fields used by template/logic
  @ViewChild('pitchCardWrapper', { static: false })
  pitchCardWrapper?: ElementRef<HTMLElement>;
  businessDetails: BusinessPitch;
  alias = environment.defaultBusinessShareLinkAlias;

  // inject services
  private formBuilder = inject(UntypedFormBuilder);
  private userCommonService = inject(UserCommonApiService);
  private businessService = inject(BusinessService);

  // signal for scaleFactor
  private scaleFactorSig: WritableSignal<number> = signal(1);

  constructor() {}

  // scaleFactor replaced by signal; expose getter for template
  get scaleFactor(): number {
    return this.scaleFactorSig();
  }

  get f() {
    return this.forgotPasswordForm.controls;
  }

  ngOnInit() {
    this.cardInitSetup();
    this.forgotPasswordForm = this.formBuilder.group({
      userName: [
        '',
        [Validators.required, Validators.pattern(AppSettings.EMAIL_PATTERN)]
      ]
    });
    window.addEventListener('resize', this.onResized);
  }

  ngAfterContentChecked() {
    this.onResized();
  }

  cardInitSetup() {
    this.userCommonService
      .getBusinessPitchModelByAlias(this.alias)
      .subscribe((companyCardData) => {
        this.businessDetails = companyCardData?.data;
      });
  }

  onResized() {
    const initialCardWidth = 370;
    if (this.pitchCardWrapper) {
      const clientWidth =
        this.pitchCardWrapper.nativeElement.parentElement.clientWidth;
      if (clientWidth - 75 < initialCardWidth) {
        this.scaleFactorSig.set((clientWidth - 75) / initialCardWidth);
      } else {
        this.scaleFactorSig.set(1);
      }
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.pitchCardWrapper.nativeElement.style.visibility = 'visible';
    }, 1000);
  }

  resetForm(): void {
    this.forgotPasswordForm?.reset();
    this.forgotPasswordForm?.markAsPristine();
    this.forgotPasswordForm?.markAsUntouched();
    this.submitted = false;
    this.errorMessage = null;
  }

  gotoOTP() {
    this.submitted = true;
    if (this.forgotPasswordForm.invalid) {
      return;
    } else {
      const forgotPasswordModel = new ForgotPasswordModel();
      forgotPasswordModel.emailId =
        this.forgotPasswordForm.controls.userName.value.trim();
      if (forgotPasswordModel.emailId) {
        this.businessService.userEmailId = forgotPasswordModel.emailId;
        this.userCommonService.forgotPassword(forgotPasswordModel).subscribe({
          next: (result) => {
            this.userCommonService.userContactNumber = result.data;
            this.userCommonService.userEmailId = forgotPasswordModel.emailId;
            this.activeTypeChange.emit('RESET_PASSWORD');
          },
          error: (error) => {
            this.errorMessage = error.Message;
          }
        });
      }
    }
  }
}
